import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, InjectionToken } from '@angular/core';
import { ImageBlock } from '../../../model';

/**
 * Optional base URL prepended to relative image `src` values.
 * Provide this token in the host app to resolve content image paths.
 */
export const IMAGE_BASE_URL = new InjectionToken<string>('IMAGE_BASE_URL');

@Component({
  selector: 'dojo-image-block-renderer',
  template: `
    <figure [class]="alignmentClass()">
      <img [src]="resolvedSrc()" [alt]="block().alt ?? ''" [title]="block().title ?? ''" [ngStyle]="imageStyle()" />
      @if (block().caption) {
        <figcaption class="mt-2 text-sm text-neutral-500">{{ block().caption }}</figcaption>
      }
    </figure>
  `,
  styles: `
    :host {
      @apply block my-4;
    }

    figure {
      @apply flex flex-col;

      &.align-left {
        @apply items-start;
      }

      &.align-center {
        @apply items-center;
      }

      &.align-right {
        @apply items-end;
      }
    }

    img {
      @apply rounded max-w-full h-auto;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgStyle],
})
export class ImageBlockRendererComponent {
  readonly block = input.required<ImageBlock>();

  private readonly imageBaseUrl = inject(IMAGE_BASE_URL, { optional: true });

  readonly resolvedSrc = computed(() => resolveImageSrc(this.block().src, this.imageBaseUrl));

  readonly alignmentClass = computed(() => {
    const align = this.block().align ?? 'center';
    return `align-${align}`;
  });

  readonly imageStyle = computed(() => {
    const block = this.block();
    const style: Record<string, string> = {};
    if (block.width !== undefined) {
      style['width'] = `${block.width}px`;
    }
    if (block.height !== undefined) {
      style['height'] = `${block.height}px`;
    }
    return style;
  });
}

/**
 * Resolve an image src against an optional base URL.
 * Returns the original src for absolute URLs and data URIs.
 */
export function resolveImageSrc(src: string, baseUrl: string | null | undefined): string {
  if (!baseUrl || src.startsWith('data:') || src.startsWith('http://') || src.startsWith('https://')) {
    return src;
  }
  return `${baseUrl}/${src}`;
}
